import { useTranslation } from 'react-i18next';

import type { GroupVisibility } from '@/shared/api/types';
import type { SearchResult, SearchResultHighlights } from '@/shared/registry/types';

import { VISIBILITIES } from '../lib/groups';

import { ArchivedBadge, GroupAvatar, GroupTypeLabel, VisibilityBadge } from './GroupBits';

/**
 * Global search hit of entity type `group` (meta: isProject, visibility, isArchived) — the content of the search page's
 * link row.
 */
export function GroupSearchResult({
  result,
  highlights,
}: {
  result: SearchResult;
  highlights?: SearchResultHighlights;
}) {
  const { t } = useTranslation('groups');
  const meta = result.meta ?? {};
  const visibility: GroupVisibility | undefined = VISIBILITIES.find((v) => v === meta.visibility);
  const snippet = highlights?.snippet ?? result.snippet;
  return (
    <>
      <GroupAvatar group={{ id: result.entityId, name: result.title, avatarUrl: null }} size="md" />
      <span className="min-w-0 flex-1" data-testid="group-search-result">
        <span className="block truncate text-[0.9375rem] font-medium text-foreground">
          {highlights?.title ?? result.title}
        </span>
        <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
          <GroupTypeLabel isProject={meta.isProject === 'true'} />
          {visibility && (
            <VisibilityBadge visibility={visibility} interactive={false} className="px-1.5 py-0" />
          )}
          {meta.isArchived === 'true' && <ArchivedBadge className="px-1.5 py-0" />}
        </span>
        {snippet && <span className="mt-1 line-clamp-2 block text-sm text-muted-foreground">{snippet}</span>}
        <span className="sr-only">{t('search.result')}</span>
      </span>
    </>
  );
}
